import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DeleteStatus, getListPaymentMethod, ItemMode, PaymentMethod, PaymentType, ServiceCardStatus, SharedService } from '../../shared/shared.service';
import moment from 'moment/moment';
import { ProxyDynamicService } from '../../shared/service/proxy-dynamic.service';
import { distinctUntilChanged, forkJoin } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-cash-transaction-item',
  templateUrl: './cash-transaction-item.component.html',
  styleUrl: './cash-transaction-item.component.scss'
})
export class CashTransactionItemComponent implements OnInit, AfterViewInit {
  readonly nzModalData = inject(NZ_MODAL_DATA);
  formGroup!: FormGroup;
  itemMode = ItemMode;
  mode = ItemMode.Add;
  data!: any;

  listPaymentMethodCache: any[] = getListPaymentMethod().filter(r => r.value !== PaymentMethod.Wallet_Voucher && r.value !== PaymentMethod.Wallet);
  listPaymentMethod: any[] = [];
  listPaymentType: any[] = [];
  listServiceCard: any[] = [];
  listOwner: any[] = [];
  listPayment: any[] = [];

  totalDue: number = 0;
  currentDateMax = moment().endOf('day').toDate();
  keywordServiceCard: string = '';
  cancelSearchServiceCard: boolean = false;
  ownerId: any;
  dataView: any;
  deleteStatus = DeleteStatus
  get f() {
    return this.formGroup.controls;
  }

  constructor(
    private service: ProxyDynamicService,
    private modalRef: NzModalRef,
    private noti: NzNotificationService,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private sharedService: SharedService,
  ) {
    this.mode = this.nzModalData.mode;
    if (this.nzModalData.data) {
      this.data = JSON.parse(JSON.stringify(this.nzModalData.data));
    }
  }

  ngOnInit() {
    this.formGroup = this.fb.group({
      id: [undefined],
      deposit_date: [null, Validators.required],
      payment_type_id: [null, Validators.required],
      service_card_id: [null, Validators.required],
      owner: [null],
      apartment_ids: [null],
      sum: [null, [Validators.required, Validators.min(1)]],
      payment_method: [null, Validators.required],
      payer: [null, Validators.required],
      ref_no: [null],
      voucher_no: [null],
      voucher_amount: [null],
      expired_date: [null],
      note: [null]
    })

    this.service.search('payment_type', {
      fields: ['*'],
      filter: { deleted: { '_eq': DeleteStatus.NO } }
    }).then((res: any) => {
      this.listPaymentType = res.map((item: any) => {
        item['text'] = item.name;
        item['value'] = item.id;
        return item;
      });
    });

  }

  ngAfterViewInit() {
    this.f['deposit_date'].setValue(this.currentDateMax);
    this.f['owner'].disable();
    this.f['apartment_ids'].disable();
    this.f['voucher_no'].disable();
    this.f['voucher_amount'].disable();
    this.f['expired_date'].disable();

    if (this.mode === ItemMode.Edit) {
      this.f['payment_type_id'].disable();
      this.f['payment_method'].disable();
      this.getCashInfo()
    }

    if (this.mode === ItemMode.View) {
      this.service.findById('cash_transaction', this.data?.id, {
        fields: ['*', 'service_card_id.owner_id.code', 'service_card_id.owner_id.name',
          'service_card_id.butler_id.code', 'service_card_id.butler_id.name',
          'user_created.first_name', 'user_created.last_name', 'user_updated.first_name', 'user_updated.last_name']
      }).then((res: any) => {
        if (res) {
          if (res.service_card_id?.owner_id) {
            res.owner = [res.service_card_id.owner_id.code, res.service_card_id.owner_id.name].join(' - ');
          } else if (res.service_card_id?.butler_id) {
            res.owner = [res.service_card_id.butler_id.code, res.service_card_id.butler_id.name].join(' - ');
          }
        }
        this.dataView = {
          ...this.data,
          ...res,
          creator: [res.user_created?.first_name, res.user_created?.last_name].filter(Boolean).join(' '),
          updater: [res.user_updated?.first_name, res.user_updated?.last_name].filter(Boolean).join(' '),
        };
      });
    }

    this.f['payment_type_id'].valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      if (value) {
        const paymentTypeSelected = this.listPaymentType.find(r => r.value === value);
        if (!paymentTypeSelected) {
          return;
        }
        const paymentMethods = paymentTypeSelected.payment_method.split(",");
        this.listPaymentMethod = this.listPaymentMethodCache.filter(item => paymentMethods.includes(item.value))
        if (paymentTypeSelected.code === PaymentType.Direct) {
          this.f['payment_method'].setValue(null, { emitEvent: false });
          this.f['ref_no'].enable();
          this.f['voucher_no'].disable();
          this.f['voucher_amount'].disable();
          this.f['voucher_no'].removeValidators(Validators.required);
          this.f['voucher_amount'].removeValidators([Validators.required, Validators.min(1)]);
          this.f['expired_date'].disable();
          this.f['voucher_no'].setValue(null, { emitEvent: false });
          this.f['voucher_amount'].setValue(null, { emitEvent: false });
          this.f['expired_date'].setValue(null, { emitEvent: false });
        } else if (paymentTypeSelected.code === PaymentType.Voucher) {
          this.f['ref_no'].setValue(null, { emitEvent: false });
          this.f['ref_no'].disable();
          this.f['voucher_no'].addValidators(Validators.required);
          this.f['voucher_amount'].addValidators([Validators.required, Validators.min(1)]);
          this.f['voucher_no'].enable();
          this.f['voucher_amount'].enable();
          this.f['expired_date'].enable();
          this.f['payment_method'].setValue(PaymentMethod.Voucher);
        }
      }
      else {
        this.listPaymentMethod = [];
        this.f['payment_method'].setValue(null);
      }
    });

    this.f['service_card_id'].valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      if (value) {
        this.getCardInfo(value);
      } else {
        this.f['owner'].setValue(null, { emitEvent: false });
        this.f['apartment_ids'].setValue(null, { emitEvent: false });
        this.ownerId = null;
      }
    })
  }

  getCardInfo(id: string) {
    this.service.findById('service_card', id, {
      fields: ['*', 'owner_id.code', 'owner_id.name', 'butler_id.code', 'butler_id.name',
        'apartment_ids.apartment_id.id', 'apartment_ids.apartment_id.code', 'apartment_ids.deleted']
    }).then(res => {
      if (res) {
        if (res['owner_id']) {
          this.f['owner'].setValue([res['owner_id']?.code, res['owner_id']?.name].join(' - '), { emitEvent: false });
        } else {
          this.f['owner'].setValue([res['butler_id']?.code, res['butler_id']?.name].join(' - '), { emitEvent: false });
        }

        if (res['apartment_ids']) {
          const listApartmentByCard = res['apartment_ids'].filter((item: any) => item.deleted === DeleteStatus.NO)
          .map((item: any) => item.apartment_id).map((item: any) => item.code).join(', ');
          this.f['apartment_ids'].setValue(listApartmentByCard, { emitEvent: false });
        }
      }
    }, (error) => {
      this.service.handleError(error)
    })
  }

  getCashInfo() {
    this.service.findById('cash_transaction', this.data?.id,
      {
        fields: ['*']
      }
    ).then((res: any) => {
      const formData = {
        ...this.data,
        ...res,
        sum: Number(this.data.sum)
      }
      this.formGroup.patchValue(formData);
      this.f['service_card_id'].disable({ emitEvent: false });
      this.f['deposit_date'].setValue(formData.deposit_date ? moment(formData.deposit_date).utc(true).toDate() : null, { emitEvent: false });
      this.f['expired_date'].setValue(formData.expired_date ? moment(formData.expired_date).utc(true).toDate() : null, { emitEvent: false });
      this.f['payment_method'].setValue(formData.payment_method, { emitEvent: false });
      this.filterDataServiceCard(formData.card_no);
      this.getCardInfo(formData.card_id);
    }).catch((error) => {
      this.service.handleError(error);
    })
  }

  editMode() {
    this.mode = ItemMode.Edit;
    this.formGroup.enable({ emitEvent: false });
    this.f['owner'].disable();
    this.f['apartment_ids'].disable();
    this.f['payment_type_id'].disable();
    this.f['payment_method'].disable();
    this.getCashInfo()
  }

  destroyModal() {
    this.modalRef.destroy()
  }

  onSave() {
    if (this.formGroup.valid) {
      this.sharedService.showLoading(true);
      const body = this.formGroup.getRawValue();
      body.deposit_date = body.deposit_date ? moment(body.deposit_date).format('YYYY-MM-DD') : null;
      body.expired_date = body.expired_date ? moment(body.expired_date).format('YYYY-MM-DD') : null;
      body.transaction_details = [{
        discount: 0,
        night_count: 1,
        is_discount_percent: false,
        price: body.sum,
      }];
      if (this.mode === ItemMode.Add) {
        body.sum = undefined;
        body.apartment_ids = undefined;
        body.owner = undefined;
        this.service.createCashTransaction(body).subscribe((res: any) => {
          if (res) {
            this.sharedService.showLoading(false);
            this.modalRef.destroy(true);
          }
        }, (error: any) => {
          this.service.handleError(error)
        });
      } else if (this.mode === ItemMode.Edit) {
        body.sum = undefined;
        body.apartment_ids = undefined;
        body.owner = undefined;
        this.service.updateCashTransaction(body.id, body).subscribe((res: any) => {
          if (res) {
            this.sharedService.showLoading(false);
            this.modalRef.destroy(true);
          }
        }, (error: any) => {
          this.service.handleError(error)
        });
      }
    } else {
      this.sharedService.showLoading(false);
      Object.values(this.formGroup.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  getMoreDataServiceCard(event: any) {
    if (!this.cancelSearchServiceCard) {
      const query = !!this.keywordServiceCard ? {
        '_or': [{ 'card_no': { '_icontains': this.keywordServiceCard } }],
        'status': { '_eq': ServiceCardStatus.Active }, 'deleted': { '_eq': DeleteStatus.NO }
      } : { 'status': { '_eq': ServiceCardStatus.Active }, 'deleted': { '_eq': DeleteStatus.NO } }
      this.service.searchWithMeta('service_card', {
        filter: query,
        limit: 100,
        page: !event ? 0 : Math.round(this.listServiceCard.length / 100),
      }).then((res: any) => {
        if (!event && !!this.keywordServiceCard) {
          this.listServiceCard = res.data.map((item: any) => {
            item['text'] = item.card_no;
            item['value'] = item.id;
            return item;
          });
        } else {
          this.listServiceCard = this.listServiceCard.concat(res.data.map((item: any) => {
            item['text'] = item.card_no;
            item['value'] = item.id;
            return item;
          }));
        }
        this.cancelSearchServiceCard = this.listServiceCard.length === Number(res.total);
      })
    }
  }

  filterDataServiceCard(event: any) {
    this.keywordServiceCard = event;
    this.cancelSearchServiceCard = false;
    if (event) {
      this.getMoreDataServiceCard(false);
    } else {
      this.listServiceCard = []
    }
  }
}
